// All of these are copied and cleaned from https://github.com/plotly/dash-bio/blob/master/src/lib/fragments/NeedlePlot.react.js
// These functions were extracted out and into their own file
import { reduce, max, range, repeat } from 'ramda';

/**
 * Checks if a variable is representation of a number or not
 * https://stackoverflow.com/questions/9716468/pure-javascript-a-function-like-jquerys-isnumeric
 * @param  {String/FLoat} n  A variable to test.
 * @return {Bool}            True if n is a number, false otherwise.
 */
export function isNumeric(maybeNumber) {
  return !isNaN(parseFloat(maybeNumber)) && isFinite(maybeNumber);
}

/**
 * Converts an array elements to numbers and ignore the elements which are not
 * a representation of  numbers
 * @param  {Array} items An array
 * @return {Array}       An array with only numbers.
 */
export function filterNanArray(items) {
  return items.filter(el => Number(isNumeric(el)));
}

/**
* Search the protein position array for small protein domains (typically 1->5 sites)
* and bogus entries (i.e. "?-123" or "320-?"), the protein domains are indicated
* by the presence of a '-' character in the element of the array.
* @param  {Array} proteinPositionArray        An array containing protein domains
* @return {Array} positionsArray          An array with only single site
            protein mutations.
* @return {Array} domains_array            An array with only small domains
            protein mutations.
* @return {Array} oldPositionsArray  An array with the indexes of the
            single site protein mutations
            relative to proteinPositionArray
* @return {Array} bogusEntryArray          An array with the indexes of the
            bogus entries (containing '?')
            relative to proteinPositionArray
*/
export function extractSmallDomains(proteinPositionArray) {
  const positionsArray = [];
  const domains_array = [];
  const oldPositionsArray = [];
  const bogusEntryArray = [];
  proteinPositionArray.forEach((dx, i) => {
    if (dx?.indexOf('-') > -1) {
      const domains_limits = dx.split('-');
      if (isNumeric(domains_limits[0]) || isNumeric(domains_limits[1])) {
        bogusEntryArray.push(i);
      } else {
        domains_array.push(dx);
      }
    } else {
      oldPositionsArray.push(i);
      positionsArray.push(dx);
    }
  });
  return [positionsArray, domains_array, oldPositionsArray, bogusEntryArray];
}

/**
 * Creates two arrays to plot horizontal lines with many markers
 *
 * @param  {number} xi  start x coordinate of the line
 * @param  {number} xf  stop x coordinate of the line
 * @param  {number} y   y coordinate of the line
 * @param  {int} n      number of markers
 * @return {array} x    x coordinates of the horizontal ine
 * @return {array} y    y coordinates of the horizontal ine
 */
export function createHorizontalLine(xi, xf, y, n) {
  const dx = (xf - xi) / n;
  const N = Math.max(2, n);
  const x = range(0, N).map(i => xi + i * dx);
  return [x, repeat(y, N)];
}

/**
 * Finds the max of an array while ignoring the NaN values
 *
 * @param  {array} test_array  an array with numbers as entries
 * @return {number}            max value of the array
 */
export function nanMax(test_array) {
  return reduce(max, -Infinity, filterNanArray(test_array));
}

const VariantAnnotations = {
  synonymous: {
    color: '#0096e1',
    legend: 'Synonymous',
    priority: 5,
  },
  missense: {
    color: '#2bbb6a',
    legend: 'Missense',
    priority: 2,
  },
  'missense & splice region variant': {
    color: '#2bbb6a',
    legend: 'Missense',
    priority: 2,
  },
  frameshift: {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  'frameshift & start lost': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  nonsense: {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  'splice acceptor': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  'splice donor': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  'splice region': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 4,
  },
  'splice region variant': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 4,
  },
  'splice region variant & synonymous': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 4,
  },
  'stop lost': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  'start lost': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  'stop gain': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  'stop gained': {
    color: '#262c32',
    legend: 'Truncating',
    priority: 1,
  },
  inframe: {
    color: '#9b2bfe',
    legend: 'Inframe',
    priority: 3,
  },
  'inframe insertion': {
    color: '#9b2bfe',
    legend: 'Inframe',
    priority: 3,
  },
  'inframe deletion': {
    color: '#9b2bfe',
    legend: 'Inframe',
    priority: 3,
  },
  'conservative inframe insertion': {
    color: '#9b2bfe',
    legend: 'Inframe',
    priority: 3,
  },
  'disruptive inframe insertion': {
    color: '#9b2bfe',
    legend: 'Inframe',
    priority: 3,
  },
  'conservative inframe deletion': {
    color: '#9b2bfe',
    legend: 'Inframe',
    priority: 3,
  },
  'disruptive inframe deletion': {
    color: '#9b2bfe',
    legend: 'Inframe',
    priority: 3,
  },
  intron: {
    color: '#f3871a',
    legend: 'Intron',
    priority: 7,
  },
  '3 prime utr': {
    color: '#e95646',
    legend: 'UTR',
    priority: 6,
  },
  '3 prime utr truncation + exon loss': {
    color: '#e95646',
    legend: 'UTR',
    priority: 6,
  },
  '5 prime utr premature start codon gain': {
    color: '#e95646',
    legend: 'UTR',
    priority: 6,
  },
  '5 prime utr': {
    color: '#e95646',
    legend: 'UTR',
    priority: 6,
  },
  '5 prime utr truncation + exon loss': {
    color: '#e95646',
    legend: 'UTR',
    priority: 6,
  },
  chromosome: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  duplication: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  inversion: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'coding sequence': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'downstream gene': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  exon: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'exon loss': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  gene: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'feature ablation': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'gene fusion': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'bidirectional gene fusion': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'rearranged at dna level': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'intergenic region': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'conserved intergenic': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  intragenic: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'conserved intron': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  mirna: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'initiator codon': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'stop retained': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'protein protein contact': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'structural interaction': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'rare amino acid': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'start retained': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  transcript: {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'regulatory region': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'upstream gene': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
  'sequence feature + exon loss': {
    color: '#7d8996',
    legend: 'Other',
    priority: 8,
  },
};

export function getVariantType(group) {
  const normalize = g => g.replace(/_/g, ' ').toLowerCase();

  return (
    (group && VariantAnnotations[normalize(group)]) || {
      legend: group,
      color: '7d8996',
      priority: 8,
    }
  );
}
